import React, { useEffect, useMemo, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Cell, Legend, Pie, PieChart, Tooltip } from "recharts";

import { useSession } from "../../../contexts/SessionContext";
import translate from "../../../services/Translate";
import { fetchPrecorrectionAccuracy, getAvailableStatisticsTypes, getChartData } from "./EvaluationAccuracy.function";

const InsightsEvaluation = () => {
  const { language } = useSession();
  const [precorrectionAccuracy, setPrecorrectionAccuracy] = useState<any>(null);
  const [statisticsType, setStatisticsType] = useState<string>("overall");
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);
  const stats = useMemo(
    () => precorrectionAccuracy?.precorrection_accuracy?.statistics?.[statisticsType],
    [precorrectionAccuracy, statisticsType]
  );

  useEffect(() => {
    fetchPrecorrectionAccuracy(startDate, endDate, setPrecorrectionAccuracy, setLoading, language);
  }, [startDate, endDate, language]);

  const _renderCustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;

    const data = payload[0].payload;
    return (
      <div className="m-regular p-3 white-background border">
        <div>
          {translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.GRAPH.VALUE")}: {data.value}
        </div>
      </div>
    );
  };

  const _renderCustomLegend = (data: any[]) => {
    return (
      <div className="text-center">
        <div className="d-flex justify-content-around m-bold  flex-wrap">
          {data.map((item, index) => (
            <div key={`legend-item-${index}`} style={{ color: item.color }}>
              {item.metric}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="p-md-5 p-3">
      <h1>{translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.TITLE")}</h1>
      <div className="d-flex justify-content-between align-items-end mb-4">
        <div className="d-flex flex-column align-items-center mt-4">
          <div className="m-bold">
            <label>{translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.EVALUATION_TYPE")}</label>
            <select
              className="form-control"
              value={statisticsType}
              onChange={(event) => setStatisticsType(event.target.value)}
            >
              {getAvailableStatisticsTypes(precorrectionAccuracy).map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="d-flex m-bold gap-3">
          <div>
            <label>{translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.START_DATE")}</label>
            <input
              type="date"
              className="form-control"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
            />
          </div>
          <div>
            <label>{translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.END_DATE")}</label>
            <input
              type="date"
              className="form-control"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
            />
          </div>
        </div>
      </div>
      {loading ? (
        <Spinner />
      ) : (
        <div className="d-flex justify-content-center">
          <div className="d-flex flex-column align-items-center">
            <h2 className="xl-bold">{translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.GRAPH.TITLE")}</h2>
            {!stats || getChartData(stats, language).length === 0 ? (
              <div className="text-center">
                <div className="m-regular">
                  {translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.GRAPH.NO_DATA_AVAILABLE")}
                </div>
              </div>
            ) : (
              <React.Fragment>
                <div className="mb-3 text-center">
                  <div className="m-regular">
                    {translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.GRAPH.TOTAL_ATTEMPTS")}:{" "}
                    {stats.total_attempts}
                  </div>
                  <div className="m-regular">
                    {translate(language, "PAGES.INSIGHTS.EVALUATION_ACCURACY.GRAPH.CORRECT_PRECORRECTION")}:{" "}
                    {stats.correct_precorrection}
                  </div>
                </div>
                <div className="d-flex align-items-center gap-3">
                  <PieChart width={300} height={250}>
                    <Pie
                      data={getChartData(stats, language)}
                      cx={150}
                      cy={100}
                      labelLine={false}
                      label={({ value }) => `${value.toFixed(1)}%`}
                      outerRadius={80}
                      fill="#8884d8"
                      dataKey="percentage"
                    >
                      {getChartData(stats, language).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip content={_renderCustomTooltip} />
                    <Legend content={() => _renderCustomLegend(getChartData(stats, language))} />
                  </PieChart>
                </div>
              </React.Fragment>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default InsightsEvaluation;
